import { useEffect, useState, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Loader2, CheckCircle2, XCircle, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { fetchBillingCheckoutStatus } from "@/lib/api";

const POLL_INTERVAL_MS = 2000;
const MAX_ATTEMPTS = 30;

export default function BillingSuccess() {
    const navigate = useNavigate();
    const [params] = useSearchParams();
    const sessionId = params.get("session_id");
    const [status, setStatus] = useState("polling"); // polling | paid | failed | timeout
    const [planName, setPlanName] = useState("");
    const attemptsRef = useRef(0);
    const stopRef = useRef(false);

    useEffect(() => {
        if (!sessionId) {
            setStatus("failed");
            return;
        }
        const poll = async () => {
            if (stopRef.current) return;
            try {
                const data = await fetchBillingCheckoutStatus(sessionId);
                if (data.payment_status === "paid") {
                    setPlanName(data.package_id || "");
                    setStatus("paid");
                    stopRef.current = true;
                    return;
                }
                if (data.status === "expired") {
                    setStatus("failed");
                    stopRef.current = true;
                    return;
                }
            } catch (err) {
                if (err?.response?.status === 404) {
                    setStatus("failed");
                    stopRef.current = true;
                    return;
                }
            }
            attemptsRef.current += 1;
            if (attemptsRef.current >= MAX_ATTEMPTS) {
                setStatus("timeout");
                stopRef.current = true;
                return;
            }
            setTimeout(poll, POLL_INTERVAL_MS);
        };
        poll();
        return () => { stopRef.current = true; };
    }, [sessionId]);

    return (
        <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center bg-gradient-to-br from-slate-50 via-blue-50/30 to-purple-50/30 px-4 py-12">
            <div className="w-full max-w-lg bg-white rounded-2xl shadow-xl shadow-slate-900/5 border border-slate-200/70 p-8 text-center" data-testid="billing-success-card">
                {status === "polling" && (
                    <>
                        <Loader2 className="w-12 h-12 text-blue-600 mx-auto animate-spin" />
                        <h1 className="mt-5 text-2xl font-bold text-slate-900">Vi bekræfter din betaling…</h1>
                        <p className="mt-2 text-slate-500">Et øjeblik mens vi taler med Stripe.</p>
                    </>
                )}
                {status === "paid" && (
                    <>
                        <div className="w-14 h-14 rounded-full bg-emerald-100 text-emerald-600 inline-flex items-center justify-center">
                            <CheckCircle2 className="w-8 h-8" />
                        </div>
                        <h1 className="mt-5 text-2xl font-bold text-slate-900">Tak for din betaling!</h1>
                        <p className="mt-2 text-slate-600">
                            Dit {planName ? <span className="font-semibold capitalize">{planName}</span> : "abonnement"}-abonnement er aktivt. Velkommen til MyServiceHelper.
                        </p>
                        <Button
                            data-testid="billing-success-dashboard-btn"
                            onClick={() => navigate("/dashboard")}
                            className="mt-6 h-11 px-6 rounded-full bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white shadow-md"
                        >
                            Gå til dashboard
                            <ArrowRight className="w-4 h-4 ml-2" />
                        </Button>
                    </>
                )}
                {status === "failed" && (
                    <>
                        <div className="w-14 h-14 rounded-full bg-red-100 text-red-600 inline-flex items-center justify-center">
                            <XCircle className="w-8 h-8" />
                        </div>
                        <h1 className="mt-5 text-2xl font-bold text-slate-900">Betalingen kunne ikke bekræftes</h1>
                        <p className="mt-2 text-slate-600">Sessionen er ugyldig eller udløbet. Prøv venligst igen fra prisplanen.</p>
                        <Button data-testid="billing-success-retry-btn" onClick={() => navigate("/priser")} variant="outline" className="mt-6 h-11 px-6 rounded-full">
                            Tilbage til priser
                        </Button>
                    </>
                )}
                {status === "timeout" && (
                    <>
                        <div className="w-14 h-14 rounded-full bg-amber-100 text-amber-600 inline-flex items-center justify-center">
                            <Loader2 className="w-8 h-8" />
                        </div>
                        <h1 className="mt-5 text-2xl font-bold text-slate-900">Betalingen er stadig under behandling</h1>
                        <p className="mt-2 text-slate-600">Stripe har ikke svaret endnu. Vi sender en bekræftelse på email når den er gennemført.</p>
                        <Button data-testid="billing-success-dashboard-btn" onClick={() => navigate("/dashboard")} className="mt-6 h-11 px-6 rounded-full bg-slate-900 text-white">
                            Gå til dashboard
                        </Button>
                    </>
                )}
            </div>
        </div>
    );
}
